<template>
  <!--
    Duas formas do mesmo editor:
    - `default`: o campo de formulário de sempre (rótulo, barra em cima, caixa).
    - `composer`: a caixa de conversa (resposta de chamado). Uma caixa só: o
      conteúdo de cima (`cabecalho`: as abas de modo; `rodape`: as miniaturas),
      o texto que cresce, a barra de formatação recolhida atrás do "Aa" e, no
      pé, imagem + Aa + ações extras + atalho + enviar. Enter envia; Shift/Alt/
      Cmd/Ctrl+Enter quebra a linha; em tela de toque o Enter só quebra.
    A barra e o rodapé são os MESMOS elementos nas duas formas: o composer só
    reordena (CSS `order`) e esconde o que não usa.
  -->
  <div v-explain.top="explanation" class="form-group" :class="{ composer: isComposer, 'composer--off': isComposer && isDisabled }">
    <div v-if="!isComposer" class="editor-rotulo">
      <i v-if="isRequired" class="pi pi-exclamation-triangle text-warning" v-show="!isValid"></i>
      <i v-if="isRequired" class="pi pi-check text-success" v-show="isValid"></i>
      <span v-if="isRequired">*</span>{{ label }}
    </div>

    <div v-if="isComposer && $slots.cabecalho" class="composer-cabecalho">
      <slot name="cabecalho" />
    </div>

    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      style="display: none"
      :data-testid="`${elemId}-file-input`"
      @change="handleFileSelect"
    />

    <!-- Toolbar -->
    <div v-if="editor && showToolbarBool && (!isComposer || formatacaoAberta)" class="editor-toolbar">
      <button
        type="button"
        :data-testid="`${elemId}-bold`"
        @click="editor.chain().focus().toggleBold().run()"
        :class="{ 'is-active': editor.isActive('bold') }"
        title="Bold"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="custom-icon">
          <path d="M8.21 13c2.106 0 3.412-1.087 3.412-2.823 0-1.306-.984-2.283-2.324-2.386v-.055a2.176 2.176 0 0 0 1.852-2.14c0-1.51-1.162-2.46-3.014-2.46H3.843V13H8.21zM5.908 4.674h1.696c.963 0 1.517.451 1.517 1.244 0 .834-.629 1.32-1.73 1.32H5.908V4.673zm0 6.788V8.598h1.73c1.217 0 1.88.492 1.88 1.415 0 .943-.643 1.449-1.832 1.449H5.907z"/>
        </svg>
      </button>
      <button
        type="button"
        :data-testid="`${elemId}-italic`"
        @click="editor.chain().focus().toggleItalic().run()"
        :class="{ 'is-active': editor.isActive('italic') }"
        title="Italic"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="custom-icon">
          <path d="M7.991 11.674 9.53 4.455c.123-.595.246-.71 1.347-.807l.11-.52H7.211l-.11.52c1.06.096 1.128.212 1.005.807L6.57 11.674c-.123.595-.246.71-1.346.806l-.11.52h3.774l.11-.52c-1.06-.095-1.129-.211-1.006-.806z"/>
        </svg>
      </button>
      <button
        type="button"
        :data-testid="`${elemId}-underline`"
        @click="editor.chain().focus().toggleUnderline().run()"
        :class="{ 'is-active': editor.isActive('underline') }"
        title="Underline"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="custom-icon">
          <path d="M5.313 3.136h-1.23V9.54c0 2.105 1.47 3.623 3.917 3.623s3.917-1.518 3.917-3.623V3.136h-1.23v6.323c0 1.49-.978 2.57-2.687 2.57-1.709 0-2.687-1.08-2.687-2.57V3.136zM12.5 15h-9v-1h9v1z"/>
        </svg>
      </button>
      <button
        type="button"
        :data-testid="`${elemId}-bullet-list`"
        @click="editor.chain().focus().toggleBulletList().run()"
        :class="{ 'is-active': editor.isActive('bulletList') }"
        title="Bullet List"
      >
        <i class="pi pi-list"></i>
      </button>
      <button
        type="button"
        :data-testid="`${elemId}-ordered-list`"
        @click="editor.chain().focus().toggleOrderedList().run()"
        :class="{ 'is-active': editor.isActive('orderedList') }"
        title="Numbered List"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" class="custom-icon">
          <path fill-rule="evenodd" d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5z"/>
          <path d="M1.713 11.865v-.474H2c.217 0 .363-.137.363-.317 0-.185-.158-.31-.361-.31-.223 0-.367.152-.373.31h-.59c.016-.467.373-.787.986-.787.588-.002.954.291.957.703a.595.595 0 0 1-.492.594v.033a.615.615 0 0 1 .569.631c.003.533-.502.8-1.051.8-.656 0-1-.37-1.008-.794h.582c.008.178.186.306.422.309.254 0 .424-.145.422-.35-.002-.195-.155-.348-.414-.348h-.3zm-.004-4.699h-.604v-.035c0-.408.295-.844.958-.844.583 0 .96.326.96.756 0 .389-.257.617-.476.848l-.537.572v.03h1.054V9H1.143v-.395l.957-.99c.138-.142.293-.304.293-.508 0-.18-.147-.32-.342-.32a.33.33 0 0 0-.342.338v.041zM2.564 5h-.635V2.924h-.031l-.598.42v-.567l.629-.443h.635V5z"/>
        </svg>
      </button>
      <!-- No composer a imagem mora no pé da caixa, sempre à mão. -->
      <button
        v-if="!isComposer"
        type="button"
        :data-testid="`${elemId}-insert-image`"
        @click="triggerImageUpload"
        title="Insert Image"
      >
        <i class="pi pi-image"></i>
      </button>
    </div>

    <!-- Editor Content -->
    <EditorContent :editor="editor" :style="editorStyles" class="editor-conteudo" :aria-label="isComposer ? label : undefined" />

    <!--
      Rodapé DENTRO do campo.

      Existe para conteúdo que pertence ao campo mas não ao texto — hoje, as
      miniaturas dos anexos do chamado. Sem ele, o anexo vira um bloco solto
      logo abaixo e parece outra coisa: no formulário do Odoo a imagem está
      DENTRO da caixa da mensagem, e é assim que as pessoas leem "faz parte
      desta mensagem".

      Só existe quando alguém passa o slot; para os outros 27 domínios que usam
      este componente nada muda, nem no DOM nem no CSS.
    -->
    <div v-if="$slots.rodape" class="editor-rodape" :class="{ 'editor-rodape--off': isDisabled }">
      <slot name="rodape" />
    </div>

    <div v-if="isComposer" class="composer-barra">
      <button
        type="button"
        class="composer-icone"
        :disabled="isDisabled"
        :title="t('Anexar imagem')"
        :aria-label="t('Anexar imagem')"
        :data-testid="`${elemId}-insert-image`"
        @click="triggerImageUpload"
      >
        <i class="pi pi-image"></i>
      </button>
      <button
        v-if="showToolbarBool"
        type="button"
        class="composer-icone composer-aa"
        :class="{ 'is-active': formatacaoAberta }"
        v-explain.top="formattingExplanation"
        :disabled="isDisabled"
        :aria-label="t('Formatação')"
        :aria-pressed="formatacaoAberta"
        :data-testid="`${elemId}-formatacao`"
        @click="formatacaoAberta = !formatacaoAberta"
      >
        Aa
      </button>
      <slot name="acoes" />

      <span class="composer-atalho" aria-hidden="true">{{ atalho }}</span>

      <button
        v-explain.top="submitExplanation"
        type="button"
        class="composer-enviar"
        :disabled="!podeEnviar"
        :title="submitExplanation ? undefined : (submitLabel || t('Enviar'))"
        :data-testid="submitTestId || `${elemId}-enviar`"
        @click="enviar"
      >
        <i :class="submitLoadingBool ? 'pi pi-spin pi-spinner' : 'pi pi-send'"></i>
        <span>{{ submitLabel || t('Enviar') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, useSlots, type PropType } from 'vue';
import { useEditor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
// Note: Underline is included in StarterKit v3+ by default, no need to import separately
import { useI18n } from 'vue-i18n';

import type { Explanation, ExplanationInput } from '../explanations/types';
import { vExplain } from '../explanations/vExplain';

const props = defineProps({
  elemId: String,
  placeholder: {
    type: String,
    default: 'Digite seu texto aqui...'
  },
  label: String,
  modelValue: String,
  value: String, // Keep for backward compatibility
  ngRequired: {
    type: [String, Boolean],
    default: false
  },
  showToolbar: {
    type: [String, Boolean],
    default: false
  },
  disabled: {
    type: [String, Boolean],
    default: false
  },
  minHeight: {
    type: String,
    default: '150px'
  },
  /**
   * Manda a imagem PARA FORA em vez de embutir base64 no HTML.
   *
   * O comportamento padrão (`false`) embute a imagem como data URI dentro do
   * conteúdo — é o que os 28 domínios que usam este componente já esperam, e
   * nada muda para eles.
   *
   * Com `true`, colar / arrastar / clicar no botão de imagem emite
   * `image-file` com o `File` e NÃO insere nada no editor. Existe porque
   * base64 no corpo é inviável quando o destino é uma coluna `TEXT` (65.535
   * bytes): uma imagem de 1024x768 em base64 passa disso sozinha, e com o
   * `sql_mode` NÃO-ESTRITO da plataforma o MySQL trunca EM SILÊNCIO, levando
   * junto o texto que vinha depois. Quem liga isto guarda o arquivo em outro
   * lugar (o Blob) e o conteúdo fica só com o texto.
   */
  externalImages: {
    type: [String, Boolean],
    default: false
  },
  /** `default` (campo de formulário) ou `composer` (caixa de conversa). */
  variant: {
    type: String as () => 'default' | 'composer',
    default: 'default'
  },
  /** Só no composer: o rótulo, o estado e o testid do botão de enviar. */
  submitLabel: {
    type: String,
    default: ''
  },
  submitDisabled: {
    type: [String, Boolean],
    default: false
  },
  submitLoading: {
    type: [String, Boolean],
    default: false
  },
  submitTestId: {
    type: String,
    default: ''
  },
  /**
   * The field's Explanation — usually only while it is `disabled`, saying why
   * ("Enviando a mensagem anterior"). Skill `self-explaining-ui`.
   */
  explanation: {
    type: Object as PropType<ExplanationInput>,
    default: null
  },
  /**
   * Only in the composer: what the send button does, or — when it is
   * disabled — why. With it, the disabled button stays hoverable/focusable
   * (aria-disabled) so the reason can open.
   */
  submitExplanation: {
    type: Object as PropType<ExplanationInput>,
    default: null
  }
});

const { t } = useI18n();

const emit = defineEmits<{
  'update:modelValue': [value: string];
  'input': [value: string];
  /** Só com `externalImages` — o arquivo cru, para quem sabe onde guardá-lo. */
  'image-file': [file: File];
  /** Só no composer — Enter ou o botão de enviar. */
  'submit': [];
}>();

const isComposer = computed<boolean>(() => props.variant === 'composer');

/** No composer a formatação começa recolhida: a caixa é para escrever. */
const formatacaoAberta = ref<boolean>(false);

/** The "Aa" toggle explains itself here, once, for every composer (house level). */
const formattingExplanation = computed((): Explanation => ({
  what: t('Mostra a barra de formatação: negrito, itálico, listas.'),
  now: { kind: 'info', text: t('Atalhos: Cmd+B, Cmd+I, "- " começa uma lista.') },
}));

const submitDisabledBool = computed<boolean>(() =>
  props.submitDisabled === true || props.submitDisabled === 'true'
);

const submitLoadingBool = computed<boolean>(() =>
  props.submitLoading === true || props.submitLoading === 'true'
);

const podeEnviar = computed<boolean>(() =>
  !isDisabled.value && !submitDisabledBool.value && !submitLoadingBool.value
);

/**
 * Tela de toque não tem Shift: lá o Enter quebra a linha e quem envia é o
 * botão. Lido na hora da tecla, porque um notebook com tela de toque e
 * teclado físico continua sendo `pointer: fine`.
 */
const telaDeToque = (): boolean =>
  typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches === true;

const atalho = computed<string>(() =>
  telaDeToque() ? '' : t('Enter envia · Shift+Enter nova linha')
);

const enviar = (): void => {
  if (podeEnviar.value) emit('submit');
};

// Refs
const fileInput = ref<HTMLInputElement | null>(null);
const isUpdatingFromProp = ref<boolean>(false);
const currentContent = ref<string>('');

// Computed properties
const isRequired = computed(() =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const hasContent = computed(() => {
  if (!currentContent.value) return false;
  // Check if content is empty or just contains empty HTML tags
  const stripped = currentContent.value.replace(/<[^>]*>/g, '').trim();
  return stripped.length > 0;
});

const isValid = computed(() => {
  if (!isRequired.value) return true;
  return hasContent.value;
});

const isDisabled = computed(() =>
  props.disabled === true || props.disabled === 'true'
);

const showToolbarBool = computed(() =>
  props.showToolbar === true || props.showToolbar === 'true'
);

const slots = useSlots();

/** Com rodapé, a borda de baixo passa a ser dele — senão aparecem duas. */
const temRodape = computed<boolean>(() => Boolean(slots.rodape));

/** No composer a caixa começa com uma linha e cresce com o texto. */
const alturaMinima = computed<string>(() => (isComposer.value ? '1.5rem' : props.minHeight));

const editorStyles = computed(() => (isComposer.value ? {
  maxHeight: '12rem',
  overflowY: 'auto' as const,
  cursor: isDisabled.value ? 'not-allowed' : 'text'
} : {
  minHeight: props.minHeight,
  borderRadius: temRodape.value ? '10px 10px 0 0' : '10px',
  border: '1px solid #ccc',
  borderTop: showToolbarBool.value ? 'none' : '1px solid #ccc',
  borderBottom: temRodape.value ? 'none' : '1px solid #ccc',
  backgroundColor: isDisabled.value ? '#f5f5f5' : 'white',
  cursor: isDisabled.value ? 'not-allowed' : 'text'
}));

// Initialize editor
const editor = useEditor({
  extensions: [
    StarterKit, // Includes: Bold, Italic, Underline, Strike, Code, BulletList, OrderedList, etc.
    Image.configure({
      inline: true,
      allowBase64: true,
    }),
    Placeholder.configure({
      // Função, e não o texto: o composer troca o placeholder com o modo
      // (resposta / nota interna) sem recriar o editor.
      placeholder: () => props.placeholder,
    }),
  ],
  content: props.modelValue || props.value || '',
  editable: !isDisabled.value,
  onUpdate: ({ editor }) => {
    if (!isUpdatingFromProp.value) {
      const html = editor.getHTML();
      currentContent.value = html;
      emit('update:modelValue', html);
      emit('input', html); // Keep for backward compatibility
    }
  },
  // Enable paste handling for images
  editorProps: {
    /**
     * Composer: Enter envia. Shift+Enter e Cmd/Ctrl+Enter já viram quebra de
     * linha pelo próprio StarterKit (HardBreak); Alt/Option+Enter não, então
     * é feito aqui. Durante uma composição de acento/IME o Enter confirma o
     * caractere — nunca envia.
     */
    handleKeyDown(view, event) {
      if (!isComposer.value || event.key !== 'Enter') return false;
      if (event.isComposing || event.keyCode === 229) return false;
      if (event.shiftKey || event.metaKey || event.ctrlKey) return false;
      if (event.altKey) {
        editor.value?.commands.setHardBreak();
        return true;
      }
      if (telaDeToque()) return false;
      enviar();
      return true;
    },
    handlePaste(view, event, slice) {
      const items = Array.from(event.clipboardData?.items || []);

      for (const item of items) {
        if (item.type.indexOf('image') === 0) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file) {
            handleImageFile(file);
          }
          return true;
        }
      }
      return false;
    },
    handleDrop(view, event, slice, moved) {
      const files = Array.from(event.dataTransfer?.files || []);
      const imageFiles = files.filter(file => file.type.startsWith('image/'));

      if (imageFiles.length > 0) {
        event.preventDefault();
        imageFiles.forEach(file => handleImageFile(file));
        return true;
      }
      return false;
    },
  },
});

// Methods
const triggerImageUpload = () => {
  fileInput.value?.click();
};

const handleFileSelect = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  if (file) {
    handleImageFile(file);
  }
  // Reset input so same file can be selected again
  input.value = '';
};

const externalImagesBool = computed<boolean>(() =>
  props.externalImages === true || props.externalImages === 'true'
);

const handleImageFile = (file: File): void => {
  // Saída antes de qualquer conversão: quem escuta recebe o arquivo ORIGINAL
  // e decide o próprio tamanho, formato e destino. Comprimir aqui imporia os
  // 1024x768/JPEG que só fazem sentido para o base64 embutido.
  if (externalImagesBool.value) {
    emit('image-file', file);
    return;
  }

  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    alert('Imagem muito grande. Tamanho máximo é 10MB.');
    return;
  }

  if (!file.type.match(/^image\/(jpeg|jpg|png|gif|bmp|webp)$/i)) {
    alert('Apenas arquivos de imagem são permitidos (JPEG, PNG, GIF, BMP, WebP).');
    return;
  }

  const img = document.createElement('img');
  img.onload = () => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;

    // Compress image to max 1024x768
    const maxWidth = 1024;
    const maxHeight = 768;
    let width = img.width;
    let height = img.height;

    if (width > height) {
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
    } else {
      if (height > maxHeight) {
        width = (width * maxHeight) / height;
        height = maxHeight;
      }
    }

    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, 0, 0, width, height);

    const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);

    // Insert image into editor
    if (editor.value) {
      editor.value.chain().focus().setImage({ src: compressedBase64 }).run();
    }
  };

  img.onerror = () => {
    alert('Erro ao carregar a imagem.');
  };

  const reader = new FileReader();
  reader.onload = (e) => {
    img.src = (e.target as FileReader).result as string;
  };
  reader.readAsDataURL(file);
};

// Initialize current content
onMounted(() => {
  currentContent.value = props.modelValue || props.value || '';
});

// Watchers
watch(() => props.modelValue, (newVal) => {
  const editorContent = editor.value?.getHTML() || '';
  const newContent = newVal || '';

  if (editorContent !== newContent) {
    isUpdatingFromProp.value = true;
    editor.value?.commands.setContent(newContent);
    currentContent.value = newContent;
    isUpdatingFromProp.value = false;
  }
});

watch(() => props.value, (newVal) => {
  const editorContent = editor.value?.getHTML() || '';
  const newContent = newVal || '';

  if (editorContent !== newContent) {
    isUpdatingFromProp.value = true;
    editor.value?.commands.setContent(newContent);
    currentContent.value = newContent;
    isUpdatingFromProp.value = false;
  }
});

watch(isDisabled, (newVal) => {
  editor.value?.setEditable(!newVal);
});

/** O placeholder é decoração do ProseMirror: só redesenha com uma transação. */
watch(() => props.placeholder, () => {
  const ed = editor.value;
  if (ed) ed.view.dispatch(ed.state.tr);
});

// Lifecycle
onBeforeUnmount(() => {
  editor.value?.destroy();
});
</script>

<style scoped>
.editor-rotulo {
  white-space: nowrap;
  display: block;
  text-align: left;
  padding: 8px 12px;
  margin-bottom: 5px;
}

/* Sem margem embaixo: a barra e a caixa do texto são UMA peça (a caixa já
   tira a borda de cima quando há barra). Os 16px que havia aqui separavam as
   duas em todos os formulários. */
.editor-toolbar {
  border: 1px solid #ccc;
  padding: 5px;
  background: #f5f5f5;
  border-radius: 8px 8px 0 0;
  display: flex;
  gap: 2px;
}

.editor-toolbar button {
  padding: 5px 10px;
  border: 1px solid #ddd;
  background: white;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
}

.editor-toolbar button:hover {
  background: #e9ecef;
}

.editor-toolbar button.is-active {
  background: var(--app-primary-color);
  color: var(--app-primary-contrast-color);
  border-color: var(--app-primary-color);
}

.editor-toolbar button .custom-icon {
  vertical-align: middle;
  display: inline-block;
}

/* Ensure PrimeIcons have same size */
.editor-toolbar button .pi {
  font-size: 16px;
  vertical-align: middle;
}

/*
  O rodapé fecha a caixa do campo: sem borda de cima (o editor já tem a dele
  como separador visual) e com o arredondamento de baixo, que o editor perde
  quando há rodapé. As duas metades formam UMA caixa.
*/
.editor-rodape {
  border: 1px solid #ccc;
  border-top: 1px dashed #e3e3e3;
  border-radius: 0 0 8px 8px;
  background: #fbfcfd;
  padding: 8px 10px;
}

.editor-rodape--off {
  background: #f5f5f5;
}

/* ---------- composer: a caixa de conversa ----------
 * Uma caixa só, com borda e foco próprios. Dentro, de cima para baixo:
 * cabeçalho (abas), miniaturas, texto, formatação (se aberta), pé. */
.composer {
  display: flex;
  flex-direction: column;
  margin: 0;
  background: #ffffff;
  border: 1px solid var(--app-border-color);
  border-radius: 8px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.composer:focus-within {
  border-color: var(--app-primary-color);
  box-shadow: 0 0 0 0.2rem rgba(var(--app-primary-color-rgb), 0.2);
}

.composer--off {
  background: var(--app-bg-light);
}

.composer-cabecalho { order: 1; }
.composer .editor-rodape { order: 2; }
.composer .editor-conteudo { order: 3; }
.composer .editor-toolbar { order: 4; }
.composer-barra { order: 5; }

/* Rodapé que não tem nada a mostrar (picker sem imagem) não ocupa espaço. */
.composer .editor-rodape:has(> .anexo-picker--vazio:only-child) {
  display: none;
}

.composer .editor-rodape {
  border: none;
  border-radius: 0;
  background: transparent;
  padding: 10px 12px 0;
}

.composer .editor-toolbar {
  border: none;
  border-top: 1px solid var(--app-border-color);
  border-radius: 0;
  background: transparent;
  padding: 4px 8px;
}

.composer .editor-toolbar button {
  border-color: transparent;
  background: transparent;
  min-width: 28px;
  height: 28px;
  padding: 4px 6px;
}

.composer .editor-toolbar button:hover {
  background: var(--app-bg-light);
}

.composer-barra {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 8px 8px;
}

.composer-icone {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--app-text-secondary-color);
  font: inherit;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.composer-icone:hover:not(:disabled):not([aria-disabled='true']),
.composer-icone.is-active {
  background: var(--app-bg-light);
  color: var(--app-text-color);
}

.composer-icone:focus-visible,
.composer-enviar:focus-visible {
  outline: 2px solid var(--app-primary-color);
  outline-offset: 1px;
}

.composer-icone:disabled,
.composer-icone[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.5;
}

.composer-aa {
  font-weight: 700;
  letter-spacing: -0.02em;
}

.composer-atalho {
  margin-left: auto;
  padding: 0 8px;
  font-size: 0.72rem;
  color: var(--app-text-secondary-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.composer-enviar {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 6px;
  background: var(--app-primary-color);
  color: var(--app-primary-contrast-color);
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s;
}

.composer-enviar:hover:not(:disabled):not([aria-disabled='true']) {
  background: var(--app-primary-hover-color);
}

/* aria-disabled = disabled WITH an explanation (v-explain): same look, still hoverable. */
.composer-enviar:disabled,
.composer-enviar[aria-disabled='true'] {
  cursor: not-allowed;
  opacity: 0.45;
}

.composer :deep(.tiptap) {
  padding: 10px 12px 4px;
}

@media (max-width: 640px) {
  .composer-atalho {
    display: none;
  }

  .composer-enviar {
    margin-left: auto;
  }
}

/* TipTap Editor Styles */
:deep(.tiptap) {
  padding: 10px;
  min-height: v-bind(alturaMinima);
  word-wrap: break-word;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  outline: none;
}

:deep(.tiptap p.is-editor-empty:first-child::before) {
  content: attr(data-placeholder);
  color: #999;
  font-style: italic;
  float: left;
  height: 0;
  pointer-events: none;
}

:deep(.tiptap img) {
  max-width: 100%;
  height: auto;
  display: block;
  margin: 10px 0;
  border-radius: 5px;
}

:deep(.tiptap ul),
:deep(.tiptap ol) {
  padding-left: 20px;
}

:deep(.tiptap ul) {
  list-style-type: disc;
}

:deep(.tiptap ol) {
  list-style-type: decimal;
}

:deep(.tiptap p) {
  margin: 0.5em 0;
}

:deep(.tiptap p:first-child) {
  margin-top: 0;
}

:deep(.tiptap p:last-child) {
  margin-bottom: 0;
}
</style>
